import {useState} from "react";
import {CheckIcon, ChevronUpDownIcon} from "@heroicons/react/24/outline";
import {Button} from "@/components/ui/button.tsx";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover.tsx";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "@/components/ui/command.tsx";
import {Spinner} from "@/components/ui/spinner.tsx";
import {cn} from "@/lib/utils.ts";

export type SearchableSelectOption = {
    value: string;
    label: string;
    /** מילות מפתח נוספות לחיפוש מעבר לתווית (לדוגמה חלקי טקסט נוספים) */
    keywords?: string;
};

type SearchableSelectProps = {
    options: SearchableSelectOption[];
    value?: string;
    onValueChange: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    disabled?: boolean;
    isLoading?: boolean;
    className?: string;
    filter?: (value: string, search: string) => number;
};

/** בחירת ערך מתוך רשימה ארוכה, עם חיפוש טקסט חופשי המסנן את הרשימה תוך כדי הקלדה (shadcn Command/Popover) */
export function SearchableSelect({
    options, value, onValueChange, placeholder = 'בחר...', searchPlaceholder = 'חיפוש...',
    emptyText = 'לא נמצאו תוצאות', disabled, isLoading, className, filter,
}: SearchableSelectProps) {
    const [open, setOpen] = useState(false);
    const selected = options.find((o) => o.value === value);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled || isLoading}
                    className={cn("w-full justify-between font-normal h-10 min-w-0", className)}
                >
                    <span className="truncate text-right flex-1">
                        {isLoading ? <Spinner size="sm"/> : (selected ? selected.label : placeholder)}
                    </span>
                    <ChevronUpDownIcon className="size-4 opacity-50 shrink-0"/>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command dir="rtl" filter={filter}>
                    <CommandInput placeholder={searchPlaceholder} className="text-sm" dir="rtl"/>
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                            {options.map((opt) => (
                                <CommandItem
                                    key={opt.value}
                                    value={`${opt.label} ${opt.keywords ?? ''}`}
                                    onSelect={() => {
                                        onValueChange(opt.value);
                                        setOpen(false);
                                    }}
                                    className="text-sm"
                                >
                                    <CheckIcon className={cn("size-4 ml-2 shrink-0", opt.value === value ? "opacity-100" : "opacity-0")}/>
                                    <span className="truncate">{opt.label}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
